import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './editor.module.css';

interface Props {
  src: string;
  x: number;
  y: number;
  onChange: (x: number, y: number) => void;
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/**
 * Pick the focal point of a picture: drag the marker over the image (mouse, touch or pen) or use
 * the two sliders. The values are normalized (0..1) and become `object-position`.
 */
export function FocalPoint({ src, x, y, onChange }: Props) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const pick = (clientX: number, clientY: number) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    onChange(
      Math.round(clamp01((clientX - rect.left) / rect.width) * 100) / 100,
      Math.round(clamp01((clientY - rect.top) / rect.height) * 100) / 100,
    );
  };

  return (
    <div className={styles.section}>
      <div
        ref={box}
        className={styles.focal}
        data-testid="focal-box"
        onPointerDown={(event) => {
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          pick(event.clientX, event.clientY);
        }}
        onPointerMove={(event) => {
          if (dragging.current) pick(event.clientX, event.clientY);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          style={{ objectPosition: `${x * 100}% ${y * 100}%` }}
        />
        <span
          className={styles.focalMarker}
          style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
          aria-hidden
        />
      </div>
      <p className={styles.hint}>{t('appearance.focalHint')}</p>
      <div className={styles.row}>
        <label className={styles.slider}>
          <span>{t('appearance.focalX')}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(x * 100)}
            onChange={(event) => {
              onChange(Number(event.target.value) / 100, y);
            }}
          />
        </label>
        <label className={styles.slider}>
          <span>{t('appearance.focalY')}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(y * 100)}
            onChange={(event) => {
              onChange(x, Number(event.target.value) / 100);
            }}
          />
        </label>
      </div>
    </div>
  );
}
